import React, { useMemo, useState } from 'react';
import { useSet } from 'react-use';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useRouteParams } from '../../../../hooks/useRouteParams';
import { usePositionsDate } from '../../../../hooks/usePositionsDate';
import { useAlternativeDetailQuery } from '../../../../api/useAlternativeDetailQuery';
import { HtmlTable } from '../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { PerformanceTableRow } from '../../../../components/PerformanceDetails/PerformanceDetails.styles';
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from '../../../../../CommonUtils/formatNumber';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../../../../../CommonComponents/OPL/Text/TruncatedText';
import { HEADERS, Sorting, SortingDirection, sortValues } from './AlternativeDetail.meta';
import { selectShowAlternativeDetail } from '../../../../../CommonSelectors/CommonAreaSelector';
import { RawButton } from '../../../../../CommonComponents/OPL/Button/Button.styles';

export const AlternativeDetail = () => {
  const showAlternativeDetail = useSelector(selectShowAlternativeDetail);

  if (!showAlternativeDetail) {
    return null;
  }

  const [sorting, setSorting] = useState<Sorting>({});

  const { accountId, clientId, groupId } = useRouteParams();

  const date = usePositionsDate();

  const [, { has: isRowExpanded, toggle: toggleRow }] = useSet<string>();

  // Due to a product requirement (OP-8717) this component does not have async states.
  // We only want to show this card if the response is not empty.
  const { data: rawData } = useAlternativeDetailQuery({
    clientId,
    date,
    accountId,
    groupId,
  });

  const data = useMemo(() => {
    if (!rawData) {
      return null;
    }

    if (Object.keys(sorting).length === 0) {
      return rawData;
    }

    const sortingKey = Object.keys(sorting)[0];

    // Sort rows inside each group
    const sortedDataRows = rawData.groups.map(group => {
      const sortedRows = [...group.rows].sort((a, b) =>
        sortValues(a[sortingKey], b[sortingKey], sorting),
      );

      return { ...group, rows: sortedRows };
    });

    // Sort groups
    const sortedDataGroups = sortedDataRows.sort((a, b) =>
      sortValues(a[sortingKey], b[sortingKey], sorting),
    );

    return { ...rawData, groups: sortedDataGroups };
  }, [rawData, sorting]);

  if (!data) {
    return null;
  }

  if (data.groups.length === 0) {
    return null;
  }

  return (
    <HtmlTable columnSpacing="small">
      <table>
        <caption>Alternative Detail</caption>

        <thead>
          <tr>
            <th>
              <Text variant="tableHeader" textAlign="left">
                Name
              </Text>
            </th>

            {HEADERS.map(({ key, text, align }) => (
              <th key={key}>
                <RawButton
                  onClick={() => {
                    if (sorting[key]) {
                      setSorting(
                        sorting[key] === SortingDirection.Asc
                          ? { [key]: SortingDirection.Desc }
                          : {},
                      );
                    } else {
                      setSorting({ [key]: SortingDirection.Asc });
                    }
                  }}
                  aria-label="Sort by column"
                  css="width: 100%"
                >
                  <Box
                    display="flex"
                    justifyContent={align === 'right' ? 'flex-end' : 'flex-start'}
                    alignItems="center"
                    css="gap: 4px"
                    p="2"
                  >
                    <Text variant="tableHeader">{text}</Text>

                    {(sorting[key] === SortingDirection.Asc ||
                      sorting[key] === SortingDirection.Desc) && (
                      <Icon
                        className={
                          sorting[key] === SortingDirection.Asc
                            ? 'fas fa-arrow-up'
                            : 'fas fa-arrow-down'
                        }
                        color="neutral.500"
                      />
                    )}
                  </Box>
                </RawButton>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data.groups.map(group => {
            const hasChildRows = group.rows.length > 0;
            const isExpanded = isRowExpanded(group.name);
            const tableRowVariant = hasChildRows ? 'clickable' : 'default';

            return (
              <React.Fragment key={group.name}>
                <PerformanceTableRow
                  isExpanded={isExpanded}
                  onClick={hasChildRows ? () => toggleRow(group.name) : undefined}
                  variant={tableRowVariant}
                >
                  <td>
                    <Box display="flex" alignItems="center">
                      {hasChildRows && (
                        <Icon
                          className={isExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
                          color="neutral.400"
                          mr={isExpanded ? 8 : 12}
                        />
                      )}
                      <TruncatedText fontWeight={600} width="100%">
                        {group.name}
                      </TruncatedText>
                    </Box>
                  </td>
                  <td />
                  <td>
                    <Text variant="tabular" fontWeight={600} textAlign="right">
                      {formatCurrency(group.committedCapital, { mantissa: 2 })}
                    </Text>
                  </td>
                  <td>
                    <Text variant="tabular" fontWeight={600} textAlign="right">
                      {formatCurrency(group.contributedCapital, { mantissa: 2 })}
                    </Text>
                  </td>
                  <td>
                    <Text variant="tabular" fontWeight={600} textAlign="right">
                      {formatCurrency(group.totalCashDistribution, { mantissa: 2 })}
                    </Text>
                  </td>
                  <td>
                    <Text variant="tabular" fontWeight={600} textAlign="right">
                      {formatCurrency(group.value, { mantissa: 2 })}
                    </Text>
                  </td>
                  <td />
                  <td />
                  <td />
                </PerformanceTableRow>

                {isExpanded &&
                  group.rows &&
                  group.rows.map(row => (
                    <PerformanceTableRow key={row.name} variant="default">
                      <td>
                        <TruncatedText pl={20} width="100%">
                          {row.name}
                        </TruncatedText>
                      </td>
                      <td>
                        <Text>
                          {row.initialInvestmentDate
                            ? moment.utc(row.initialInvestmentDate).format('MM/DD/YYYY')
                            : ''}
                        </Text>
                      </td>
                      <td>
                        <Text variant="tabular" textAlign="right">
                          {formatCurrency(row.committedCapital, { mantissa: 2 })}
                        </Text>
                      </td>
                      <td>
                        <Text variant="tabular" textAlign="right">
                          {formatCurrency(row.contributedCapital, { mantissa: 2 })}
                        </Text>
                      </td>
                      <td>
                        <Text variant="tabular" textAlign="right">
                          {formatCurrency(row.totalCashDistribution, { mantissa: 2 })}
                        </Text>
                      </td>
                      <td>
                        <Text variant="tabular" textAlign="right">
                          {formatCurrency(row.value, { mantissa: 2 })}
                        </Text>
                      </td>
                      <td>
                        <Text>
                          {row.valuationDate
                            ? moment.utc(row.valuationDate).format('MM/DD/YYYY')
                            : ''}
                        </Text>
                      </td>
                      <td>
                        <Text variant="tabular" textAlign="right">
                          {formatNumber(row.multiple, { mantissa: 2 })}
                        </Text>
                      </td>
                      <td>
                        <Text variant="tabular" textAlign="right">
                          {row.return !== null ? formatPercentage(row.return, { mantissa: 2 }) : ''}
                        </Text>
                      </td>
                    </PerformanceTableRow>
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </HtmlTable>
  );
};
